import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import * as ozon from "@/api/ozon_fbs";
import * as pickersApi from "@/api/one_c_pickers";
import { PickerField } from "@/components/pickers/PickerField";
import { PickerRow, RemotePicker, savedPickerValue } from "@/components/pickers/RemotePicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toastError } from "@/lib/api-errors";

const searchWarehouses = (q: string): Promise<PickerRow[]> =>
  pickersApi.searchStructuralUnits("Склад", q);

/** Новый кабинет. Остальные поля необязательны, а форма читает их через `?? ""`
 *  — перечислять двадцать пустых строк значило бы держать шестую копию списка. */
const EMPTY: ozon.OzonCabinetPayload = { name: "" };

/** Кабинет → форма: это ровно кабинет минус то, что считает сервер.
 *
 * ⚠️ Раньше здесь перечислялись все поля и заодно `null` серверных ссылок
 * переписывались в `""` при КАЖДОМ сохранении — на чтении они всё равно идут
 * через `?? ""`, так что смысла в этом не было, а обнулённая ссылка уезжала
 * обратно пустой строкой. */
function toPayload(c: ozon.OzonCabinet): ozon.OzonCabinetPayload {
  const { id: _id, keys_ready: _k, keys_error: _ke, keys_in_portal: _kp,
    requisites_ready: _r, requisites_error: _re, ...rest } = c;
  return rest;
}

export function OzonCabinetsCard(): React.ReactElement {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [form, setForm] = useState<ozon.OzonCabinetPayload>(EMPTY);

  const cabinets = useQuery({ queryKey: ["ozon", "cabinets"], queryFn: ozon.listCabinets });
  // Где лежат ключи и есть ли ПТМО — свойства ПРОФИЛЯ, а не кабинета: форма
  // нового кабинета должна знать это до того, как появится первая строка.
  const config = useQuery({ queryKey: ["ozon", "config"], queryFn: ozon.config });
  const profile: FormProfile = {
    keysInPortal: config.data?.keys_in_portal ?? false,
    ptmo: config.data?.ptmo ?? true,
  };
  const done = () => {
    setEditing(null);
    void qc.invalidateQueries({ queryKey: ["ozon", "cabinets"] });
  };

  const save = useMutation({
    mutationFn: (v: { id: number | "new"; payload: ozon.OzonCabinetPayload }) =>
      v.id === "new" ? ozon.createCabinet(v.payload) : ozon.updateCabinet(v.id, v.payload),
    onSuccess: () => {
      toast.success("Кабинет сохранён");
      done();
    },
    onError: toastError("Не удалось выполнить"),
  });

  const remove = useMutation({
    mutationFn: ozon.deleteCabinet,
    onSuccess: () => {
      toast.success("Кабинет удалён");
      done();
    },
    onError: toastError("Не удалось выполнить"),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Кабинеты Ozon</CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setForm(EMPTY);
            setEditing("new");
          }}
        >
          <Plus className="mr-1 h-4 w-4" /> Добавить
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {cabinets.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {cabinets.isError && (
          <p className="text-sm text-destructive">Не удалось загрузить кабинеты.</p>
        )}
        {config.isError && (
          <p className="text-sm text-destructive">
            Не удалось загрузить настройки раздела — форма кабинета недоступна.
          </p>
        )}
        {cabinets.data?.length === 0 && !cabinets.isLoading && (
          <p className="text-sm text-muted-foreground">
            {profile.keysInPortal
              ? "Кабинетов нет. Заведите кабинет и введите его ключи Ozon (Client-Id и API-Key)."
              : "Кабинетов нет. Заведите кабинет и укажите папку Infisical с ключами Ozon."}
          </p>
        )}

        {cabinets.data?.map((c) => (
          <div key={c.id} className="rounded-md border p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate font-medium">{c.name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {c.organization_name || "организация не выбрана"}
                </div>
                <CabinetWarehousesLine cabinet={c} />
                {!c.keys_ready && (
                  <div className="mt-1 text-xs text-destructive">{c.keys_error}</div>
                )}
                {c.keys_ready && !c.requisites_ready && (
                  <div className="mt-1 text-xs text-amber-600">{c.requisites_error}</div>
                )}
              </div>
              <div className="flex shrink-0 gap-1">
                <TestButton cabinet={c} />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    setForm(toPayload(c));
                    setEditing(c.id);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove.mutate(c.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {editing === c.id && config.data && (
              <CabinetForm
                profile={profile}
                value={form}
                cabinetId={c.id}
                onChange={setForm}
                onCancel={() => setEditing(null)}
                onSave={() => save.mutate({ id: c.id, payload: form })}
                saving={save.isPending}
              />
            )}
          </div>
        ))}

        {editing === "new" && config.data && (
          <div className="rounded-md border p-3">
            <CabinetForm
              profile={profile}
              value={form}
              cabinetId={null}
              onChange={setForm}
              onCancel={() => setEditing(null)}
              onSave={() => save.mutate({ id: "new", payload: form })}
              saving={save.isPending}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Кнопка «Проверить».
 *
 * Проверяет не только доступность ключей: тем же ответом Ozon приносит
 * настройки склада, от которых зависит наш процесс — автосборка, доверительная
 * приёмка и формат этикетки. Их и показываем, потому что иначе их пришлось бы
 * искать в кабинете Ozon руками.
 */
function TestButton({ cabinet }: { cabinet: ozon.OzonCabinet }): React.ReactElement {
  const qc = useQueryClient();
  const [result, setResult] = useState<{
    ok: boolean;
    message: string;
    warehouses: ozon.OzonWarehouse[];
  } | null>(null);

  const test = useMutation({
    mutationFn: () => ozon.testCabinet(cabinet.id),
    onSuccess: (data) => {
      setResult(data);
      // Тот же список складов, что читает пикер: «проверил → настраиваю» —
      // штатный порядок, и второй запрос в Ozon за теми же данными не нужен.
      if (data.ok) qc.setQueryData(["ozon", "warehouses", cabinet.id], data.warehouses);
    },
    onError: toastError("Не удалось выполнить"),
  });

  return (
    <div className="flex flex-col items-end gap-1">
      <Button size="sm" variant="outline" onClick={() => test.mutate()} disabled={test.isPending}>
        {test.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
        Проверить
      </Button>
      {result && (
        <div className="max-w-md text-right text-xs">
          <div className={result.ok ? "text-emerald-600" : "text-destructive"}>
            {result.message}
          </div>
          {result.warehouses.map((w) => (
            <div key={w.warehouse_id} className="text-muted-foreground">
              {w.name} — автосборка {w.is_auto_assembly ? "ВКЛ" : "выкл"}
              {w.has_entrusted_acceptance ? ", доверительная приёмка" : ""}
              {w.carriage_label_type ? `, этикетка ${w.carriage_label_type}` : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Что форме нужно знать о профиле инсталляции (`GET /ozon-fbs/config`). */
interface FormProfile {
  /** Ключи вводятся в портал (`db_fernet`) — поля ключей вместо слага Infisical. */
  keysInPortal: boolean;
  /** Нет ПТМО — нет и её реквизитов (контрагент-получатель, договор передачи). */
  ptmo: boolean;
}

function CabinetForm({
  profile,
  value,
  cabinetId,
  onChange,
  onCancel,
  onSave,
  saving,
}: {
  profile: FormProfile;
  value: ozon.OzonCabinetPayload;
  /** null — кабинет ещё не создан, справочники Ozon читать не по чему. */
  cabinetId: number | null;
  onChange: (v: ozon.OzonCabinetPayload) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}): React.ReactElement {
  const patch = (next: Partial<ozon.OzonCabinetPayload>) => onChange({ ...value, ...next });

  return (
    <div className="mt-3 grid gap-3 border-t pt-3 sm:grid-cols-2">
      <div>
        <Label className="text-xs">Название</Label>
        <Input value={value.name} onChange={(e) => patch({ name: e.target.value })} />
      </div>
      {profile.keysInPortal ? (
        // ⚠️ Поля всегда пустые: сервер ключи не возвращает ни в каком виде.
        // Пусто при сохранении = «не менять», поэтому правка кабинета без
        // повторного ввода ключей их не стирает; менять можно по одному.
        <>
          <div>
            <Label className="text-xs" htmlFor="ozon-client-id">Client-Id</Label>
            <Input
              id="ozon-client-id"
              type="password"
              autoComplete="off"
              value={value.client_id ?? ""}
              onChange={(e) => patch({ client_id: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs" htmlFor="ozon-api-key">API-Key</Label>
            <Input
              id="ozon-api-key"
              type="password"
              autoComplete="off"
              value={value.api_key ?? ""}
              onChange={(e) => patch({ api_key: e.target.value })}
            />
          </div>
          <p className="sm:col-span-2 -mt-2 text-xs text-muted-foreground">
            Ключи хранятся в базе портала зашифрованными; поля всегда пустые — введите
            новый ключ, чтобы заменить.
          </p>
        </>
      ) : (
        <div>
          <Label className="text-xs">Папка Infisical (слаг юрлица)</Label>
          <Input
            value={value.infisical_slug ?? ""}
            placeholder="omegavita"
            onChange={(e) => patch({ infisical_slug: e.target.value })}
          />
        </div>
      )}

      <OzonWarehousesField
        cabinetId={cabinetId}
        value={value.warehouses ?? []}
        onChange={(warehouses) => patch({ warehouses })}
      />

      <Requisites value={value} patch={patch} ptmo={profile.ptmo} />

      <div className="sm:col-span-2 flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Отмена
        </Button>
        <Button onClick={onSave} disabled={saving || !value.name.trim()}>
          {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
          Сохранить
        </Button>
      </div>
    </div>
  );
}

/** Склады кабинета строкой на карточке: у каждого — его комната (склад 1С).
 *  Склад без комнаты назван прямо: закрытие и завоз по нему откажут. */
function CabinetWarehousesLine({ cabinet }: { cabinet: ozon.OzonCabinet }): React.ReactElement {
  if (cabinet.warehouses.length === 0) {
    return <div className="text-xs text-amber-600">Склады Ozon не выбраны</div>;
  }
  return (
    <div className="text-xs text-muted-foreground">
      {cabinet.warehouses.map((w, i) => (
        <span key={w.ozon_warehouse_id}>
          {i > 0 && "; "}
          склад {w.name || w.ozon_warehouse_id} →{" "}
          {w.one_c_warehouse_ref ? (
            w.one_c_warehouse_name || w.one_c_warehouse_ref
          ) : (
            <span className="text-amber-600">склад 1С не выбран</span>
          )}
        </span>
      ))}
    </div>
  );
}

/**
 * Склады продавца Ozon кабинета — СПИСКОМ, как у ВБ: у каждого свой метод
 * доставки и своя комната (склад 1С). Сохраняется полной заменой `warehouses`.
 *
 * ⚠️ Склад и метод читаются из самого Ozon, а не вводятся числом: id склада
 * руками — это тот же класс ошибки, что GUID 1С руками. Пока кабинет не создан
 * (нет id) и пока ключи не заданы, справочник читать не по чему — тогда поле
 * честно говорит об этом, а не показывает пустой список как «ничего нет».
 */
function OzonWarehousesField({
  cabinetId,
  value,
  onChange,
}: {
  cabinetId: number | null;
  value: ozon.OzonCabinetWarehouse[];
  onChange: (v: ozon.OzonCabinetWarehouse[]) => void;
}): React.ReactElement {
  const warehouses = useQuery({
    queryKey: ["ozon", "warehouses", cabinetId],
    queryFn: () => ozon.cabinetWarehouses(cabinetId as number),
    enabled: cabinetId !== null,
    retry: false,
    // Холодное чтение: склады продавца меняются раз в год, а каждое открытие
    // формы — это запрос в Ozon. Правило общее с остальными справочниками
    // портала (см. staleTime у `room-stock` и `article-names` в ФБС ВБ).
    staleTime: 5 * 60_000,
  });

  if (cabinetId === null) {
    return (
      <div className="sm:col-span-2 text-xs text-muted-foreground">
        Склады Ozon и методы доставки выбираются после сохранения кабинета — сначала нужны ключи.
      </div>
    );
  }

  const patchRow = (i: number, next: Partial<ozon.OzonCabinetWarehouse>) =>
    onChange(value.map((w, idx) => (idx === i ? { ...w, ...next } : w)));
  const chosen = new Set(value.map((w) => w.ozon_warehouse_id));
  const free = (warehouses.data ?? []).filter((w) => !chosen.has(w.warehouse_id));

  return (
    <div className="sm:col-span-2">
      <Label className="text-xs">Склады продавца в Ozon</Label>
      <div className="mt-1 space-y-2">
        {value.map((wh, i) => (
          <OzonWarehouseRow
            key={wh.ozon_warehouse_id}
            cabinetId={cabinetId}
            value={wh}
            onChange={(next) => patchRow(i, next)}
            onRemove={() => onChange(value.filter((_, idx) => idx !== i))}
          />
        ))}
        {warehouses.isError ? (
          <p className="text-xs text-destructive">
            Не удалось прочитать склады Ozon — проверьте ключи кабинета.
          </p>
        ) : (
          <select
            aria-label="Добавить склад Ozon"
            className="h-9 w-full rounded-md border bg-background px-2 text-sm"
            value=""
            disabled={warehouses.isLoading}
            onChange={(e) => {
              const row = warehouses.data?.find((w) => w.warehouse_id === Number(e.target.value));
              if (!row) return;
              onChange([
                ...value,
                {
                  ozon_warehouse_id: row.warehouse_id,
                  name: row.name,
                  delivery_method_id: null,
                  delivery_method_name: "",
                  one_c_warehouse_ref: null,
                  one_c_warehouse_name: null,
                },
              ]);
            }}
          >
            <option value="">
              {warehouses.isLoading
                ? "склады Ozon загружаются…"
                : free.length
                  ? "+ добавить склад Ozon…"
                  : "все склады Ozon кабинета уже в списке"}
            </option>
            {free.map((w) => (
              <option key={w.warehouse_id} value={w.warehouse_id}>
                {w.name}
                {w.is_auto_assembly ? " (автосборка ВКЛ)" : ""}
              </option>
            ))}
          </select>
        )}
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground">
        у каждого склада свой склад 1С (комната ФБС): по нему закрываются отправления,
        заказывается завоз и выбирается точка сборки
      </p>
    </div>
  );
}

/** Строка склада Ozon: метод доставки (из Ozon, по этому складу) и склад 1С. */
function OzonWarehouseRow({
  cabinetId,
  value,
  onChange,
  onRemove,
}: {
  cabinetId: number;
  value: ozon.OzonCabinetWarehouse;
  onChange: (v: Partial<ozon.OzonCabinetWarehouse>) => void;
  onRemove: () => void;
}): React.ReactElement {
  const methods = useQuery({
    queryKey: ["ozon", "delivery-methods", cabinetId, value.ozon_warehouse_id],
    queryFn: () => ozon.cabinetDeliveryMethods(cabinetId, value.ozon_warehouse_id),
    retry: false,
    staleTime: 5 * 60_000,
  });

  return (
    <div className="grid items-start gap-2 rounded-md border p-2 sm:grid-cols-[1fr,1fr,1fr,auto]">
      <div className="text-sm">
        {value.name || `Склад ${value.ozon_warehouse_id}`}
        <div className="text-xs text-muted-foreground">{value.ozon_warehouse_id}</div>
      </div>
      {methods.isError ? (
        <p className="text-xs text-destructive">Не удалось прочитать методы доставки.</p>
      ) : (
        <select
          aria-label={`Метод доставки: ${value.name || value.ozon_warehouse_id}`}
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
          value={value.delivery_method_id ?? ""}
          onChange={(e) => {
            const id = Number(e.target.value) || null;
            const row = methods.data?.find((m) => m.id === id);
            onChange({ delivery_method_id: id, delivery_method_name: row?.name ?? "" });
          }}
        >
          <option value="">— метод доставки —</option>
          {/* Сохранённый метод, которого нет в ответе Ozon (ещё грузится или
              закрыт), всё равно назван — иначе select показал бы «не выбран». */}
          {value.delivery_method_id !== null &&
            !methods.data?.some((m) => m.id === value.delivery_method_id) && (
              <option value={value.delivery_method_id}>
                {value.delivery_method_name || value.delivery_method_id}
              </option>
            )}
          {methods.data?.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      )}
      <RemotePicker
        value={savedPickerValue(value.one_c_warehouse_ref, value.one_c_warehouse_name)}
        onChange={(r) =>
          onChange({ one_c_warehouse_ref: r?.ref ?? null, one_c_warehouse_name: r?.name ?? null })
        }
        search={searchWarehouses}
        queryKey={["one-c", "warehouses"]}
        placeholder="склад 1С (комната ФБС)"
      />
      <Button size="sm" variant="ghost" aria-label="Убрать склад" onClick={onRemove}>
        <Trash2 className="h-4 w-4 text-destructive" />
      </Button>
    </div>
  );
}

/**
 * Реквизиты 1С кабинета — те, из которых собирается цепочка документов.
 *
 * ⚠️ Все шесть заведены сразу, а не «когда дойдём до документов». В портале уже
 * был ровно этот случай: у второй инсталляции шесть ссылок остались пустыми,
 * разделы честно отвечали «не задана ссылка 1С», а ЗАДАТЬ её было нечем.
 * Сервер про незаполненные говорит на карточке (`requisites_error`), а здесь
 * их можно заполнить.
 *
 * ⚠️ Зависимые пикеры (договор ← контрагент, счёт ← организация) заблокированы
 * до выбора родителя и ОБНУЛЯЮТСЯ при его смене — иначе в кабинете остался бы
 * договор чужого юрлица. Правило перенято у настроек ВБ вместе с причиной.
 */
function Requisites({
  value,
  patch,
  ptmo,
}: {
  value: ozon.OzonCabinetPayload;
  patch: (v: Partial<ozon.OzonCabinetPayload>) => void;
  /** Нет ПТМО — пара «контрагент-получатель + договор передачи» не показывается:
   *  сервер её и не требует (`assert_requisites_ready`), как у ВБ. */
  ptmo: boolean;
}): React.ReactElement {
  const org = value.organization_ref ?? "";
  const kontragent = value.kontragent_ref ?? "";
  const saleKontragent = value.sale_kontragent_ref ?? "";

  return (
    <>
      <PickerField
        label="Организация (продавец)"
        queryKey={["one-c-organizations"]}
        value={savedPickerValue(org, value.organization_name ?? "")}
        onChange={(r) =>
          patch({
            organization_ref: r?.ref ?? "",
            organization_name: r?.name ?? "",
            // Счёт и договоры принадлежат организации — при её смене они врут.
            contract_ref: "",
            contract_name: "",
            sale_contract_ref: "",
            sale_contract_name: "",
            bank_account_ref: "",
            bank_account_name: "",
          })
        }
        search={pickersApi.searchOrganizations}
        placeholder="поиск в 1С…"
      />

      {ptmo && (
        <>
          <PickerField
            label="Контрагент-получатель (передача)"
            queryKey={["one-c-kontragents", "ozon-transfer"]}
            value={savedPickerValue(kontragent, value.kontragent_name ?? "")}
            onChange={(r) =>
              patch({
                kontragent_ref: r?.ref ?? "",
                kontragent_name: r?.name ?? "",
                contract_ref: "",
                contract_name: "",
              })
            }
            search={pickersApi.searchKontragents}
            placeholder="поиск в 1С…"
          />
          <PickerField
            label="Договор передачи"
            queryKey={["one-c-dogovors", "ozon-transfer", kontragent, org]}
            blockedHint={kontragent ? undefined : "сначала выберите контрагента-получателя"}
            value={savedPickerValue(value.contract_ref ?? "", value.contract_name ?? "")}
            onChange={(r) => patch({ contract_ref: r?.ref ?? "", contract_name: r?.name ?? "" })}
            search={(q) => pickersApi.searchDogovors(kontragent, q, { organizationRef: org })}
            placeholder="поиск в 1С…"
          />
        </>
      )}

      <PickerField
        label="Покупатель-маркетплейс"
        hint="у Озона это «ИНТЕРНЕТ РЕШЕНИЯ ООО»"
        queryKey={["one-c-kontragents", "ozon-sale"]}
        value={savedPickerValue(saleKontragent, value.sale_kontragent_name ?? "")}
        onChange={(r) =>
          patch({
            sale_kontragent_ref: r?.ref ?? "",
            sale_kontragent_name: r?.name ?? "",
            sale_contract_ref: "",
            sale_contract_name: "",
          })
        }
        search={pickersApi.searchKontragents}
        placeholder="поиск в 1С…"
      />
      <PickerField
        label="Договор продажи"
        queryKey={["one-c-dogovors", "ozon-sale", saleKontragent, org]}
        blockedHint={saleKontragent ? undefined : "сначала выберите покупателя"}
        value={savedPickerValue(value.sale_contract_ref ?? "", value.sale_contract_name ?? "")}
        onChange={(r) =>
          patch({ sale_contract_ref: r?.ref ?? "", sale_contract_name: r?.name ?? "" })
        }
        search={(q) => pickersApi.searchDogovors(saleKontragent, q, { organizationRef: org })}
        placeholder="поиск в 1С…"
      />
      <PickerField
        label="Банковский счёт"
        queryKey={["one-c-bank-accounts", org]}
        blockedHint={org ? undefined : "сначала выберите организацию"}
        value={savedPickerValue(value.bank_account_ref ?? "", value.bank_account_name ?? "")}
        onChange={(r) => patch({ bank_account_ref: r?.ref ?? "", bank_account_name: r?.name ?? "" })}
        search={(q) => pickersApi.searchBankAccounts(org, q)}
        placeholder="поиск в 1С…"
      />
    </>
  );
}
